import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../libs/utils';

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const hasTracked = useRef(false);

  // Track landing page visit event
  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    trackEvent('supaserverftueevents', { event_id: 1 });
  }, []);

  const handleGenerate = () => {
    // Track button click event
    trackEvent('supaserverftueevents', { event_id: 2 });

    if (isAuthenticated) {
      navigate('/api-request?demo=true');
    } else {
      navigate('/login', { state: { returnTo: '/api-request?demo=true' } });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO 
        title="Supaserver — Create Backend API in Seconds | No Code Required"
        description="Build REST APIs instantly without writing code. Define endpoints, store data, and go live in seconds — no servers or DevOps required. Start free today."
        canonical="https://supaserver.app/"
      />
      <Header />

      {/* Main Section */}
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-5xl lg:text-6xl font-semibold text-gray-900 leading-tight">
                  Your backend ready in seconds
                </h2>
                <p className="leading-relaxed max-w-2xl" style={{
                  fontSize: '20px',
                  lineHeight: '28px',
                  letterSpacing: '0',
                  color: '#707070'
                }}>
                  Define endpoints, store data, go live — no servers required.
                </p>
                <div className="space-x-4">
                  <button
                    onClick={handleGenerate}
                    className="px-5 py-3 text-white rounded-full font-semibold transition-colors"
                    style={{
                      fontSize: '16px',
                      lineHeight: '24px',
                      letterSpacing: '.009em',
                      fontWeight: 500,
                      cursor: 'pointer',
                      backgroundColor: '#f97316',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#ea580c';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f97316';
                    }}
                  >
                    Create backend
                  </button>
                </div>
              </div>
              <div className="flex justify-center items-center">
                <div className="text-center lg:text-left">
                  <div className="space-y-3">
                    <p className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                      One Data Store.
                    </p>
                    <p className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                      One API.
                    </p>
                    <p className="text-4xl lg:text-5xl font-bold leading-tight" style={{ color: '#f97316' }}>
                      Zero Code.
                    </p>
                    <p className="text-4xl lg:text-5xl font-bold leading-tight" style={{ color: '#f97316' }}>
                      Zero DevOps.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Schema */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-5xl mx-auto px-4 relative z-10">
            <div className="text-center mb-12">
              <h3 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                REST server. No setup needed.
              </h3>
              <p className="text-gray-400 text-lg">
                Just POST your data and GET it right back
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* POST Example */}
              <div className="bg-gray-950/50 rounded-2xl border border-gray-700/50 overflow-hidden backdrop-blur-sm">
                <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-700/50 bg-gray-800/30">
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full uppercase tracking-wider">
                    POST
                  </span>
                  <span className="text-gray-400 text-sm">your data</span>
                </div>
                <div className="p-5">
                  <pre className="text-sm leading-relaxed overflow-x-auto">
                    <code>
                      <span className="text-gray-500">$</span>{' '}
                      <span className="text-blue-400">curl</span>{' '}
                      <span className="text-yellow-400">-X POST</span>{' '}
                      <span className="text-gray-300">api.supaserver.app/test</span>{' '}
                      <span className="text-gray-500">\</span>
                      {'\n'}
                      {'  '}<span className="text-yellow-400">-H</span>{' '}
                      <span className="text-green-400">"Content-Type: application/json"</span>{' '}
                      <span className="text-gray-500">\</span>
                      {'\n'}
                      {'  '}<span className="text-yellow-400">-d</span>{' '}
                      <span className="text-orange-400">'{`{"testData": "test"}`}'</span>
                    </code>
                  </pre>
                </div>
                <div className="px-5 pb-5">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Response
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700/30">
                    <pre className="text-sm text-green-400">
                      <code>{`{"_id": "abc123", "testData": "test"}`}</code>
                    </pre>
                  </div>
                </div>
              </div>

              {/* GET Example */}
              <div className="bg-gray-950/50 rounded-2xl border border-gray-700/50 overflow-hidden backdrop-blur-sm">
                <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-700/50 bg-gray-800/30">
                  <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full uppercase tracking-wider">
                    GET
                  </span>
                  <span className="text-gray-400 text-sm">it right back</span>
                </div>
                <div className="p-5">
                  <pre className="text-sm leading-relaxed overflow-x-auto">
                    <code>
                      <span className="text-gray-500">$</span>{' '}
                      <span className="text-blue-400">curl</span>{' '}
                      <span className="text-yellow-400">-X GET</span>{' '}
                      <span className="text-gray-300">api.supaserver.app/test</span>{' '}
                      <span className="text-gray-500">\</span>
                      {'\n'}
                      {'  '}<span className="text-yellow-400">-H</span>{' '}
                      <span className="text-green-400">"Content-Type: application/json"</span>
                    </code>
                  </pre>
                </div>
                <div className="px-5 pb-5">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Response
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700/30">
                    <pre className="text-sm text-green-400">
                      <code>{`[{"_id": "abc123", "testData": "test"}]`}</code>
                    </pre>
                  </div>
                </div>
              </div>
            </div>

            {/* Arrow and explanation */}
            <div className="flex justify-center mt-8">
              <div className="flex items-center gap-4 px-6 py-3 bg-white/5 rounded-full border border-white/10">
                <span className="text-gray-400 text-sm">No servers</span>
                <span className="text-gray-600">•</span>
                <span className="text-gray-400 text-sm">No database setup</span>
                <span className="text-gray-600">•</span>
                <span className="text-gray-400 text-sm">Just works</span>
              </div>
            </div>
          </div>
        </section>

        {/* Video Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-4xl font-semibold text-center text-gray-900 mb-12">
              Build powerful workflows incredibly fast
            </h3>
            <div className="max-w-4xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
                <video
                  className="w-full h-full"
                  autoPlay
                  loop
                  muted
                  playsInline
                  poster="/demo-poster.png"
                >
                  <source src="/demo.webm" type="video/webm" />
                  <source src="/demo.mp4" type="video/mp4" />
                  <source src="/demo.mov" type="video/quicktime" />
                </video>
              </div>
            </div>
          </div>
        </section>

        {/* Enterprise Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-sm font-medium text-blue-600 uppercase tracking-wider mb-4">
              Enterprise
            </div>
            <h3 className="text-4xl lg:text-5xl font-semibold text-gray-900 mb-6">
            Scale your infrastructure, not your engineering team
            </h3>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Progress stalls when teams have to wait for technical help. Supaserver empowers
              everyone in your business to safely automate their work in minutes—not months.
            </p>
            <a 
              href="mailto:dimasauchanka@gmail.com" 
              className="inline-block px-8 py-3 bg-gray-900 text-white rounded-full font-semibold hover:bg-gray-800 transition-colors"
            >
              Contact Supaserver
            </a>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}
